import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  useMediaQuery,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import TableSortLabel from '@mui/material/TableSortLabel';
import ReactCountryFlag from 'react-country-flag';
import countryCodeMapping from '../../utils/CodeCountryMapping.json';
import Button from '@mui/material/Button';

// === GUTTER UNIFICADO PARA TODAS LAS VISTAS ===
const LEFT_GUTTER = '15px';

// === CONFIG DE GRID SOLO PARA 1200–1387 ===
const GRID_COLS_INTERMEDIATE = '80px 1fr 100px 120px 120px';
// Pos | Name | Country | Region | Total Score

const StyledTableHeadRow = styled(TableRow)(({ isIntermediateView }) => ({
  backgroundColor: '#03263D',
  border: '1px solid white',
  borderRadius: '10px',
  marginBottom: '5px',
  height: '50px',
  alignItems: 'center',
  color: 'white',
  // 👇 gutter igual que desktop SIEMPRE
  paddingLeft: LEFT_GUTTER,
  display: isIntermediateView ? 'grid' : 'flex',
  gridTemplateColumns: isIntermediateView ? GRID_COLS_INTERMEDIATE : undefined,
}));

const StyledTableRow = styled(TableRow)(({ isIntermediateView }) => ({
  backgroundColor: '#03263D',
  border: '1px solid white',
  borderRadius: '10px',
  marginBottom: '5px',
  height: '65px',
  alignItems: 'center',
  color: 'white',
  position: 'relative',
  // 👇 gutter igual que desktop SIEMPRE
  paddingLeft: LEFT_GUTTER,
  paddingRight: 0,
  display: isIntermediateView ? 'grid' : 'flex',
  gridTemplateColumns: isIntermediateView ? GRID_COLS_INTERMEDIATE : undefined,
}));

const LeftAlignTableCell = styled(TableCell)(({ isMobileView, isIntermediateView }) => ({
  fontWeight: 'bold',
  border: 'none',
  fontSize: '14px',
  padding: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  color: 'white',
  ...(isIntermediateView
    ? { width: 'auto', flex: 'unset' } // la grid manda
    : { width: isMobileView ? 'auto' : '190px', flex: isMobileView ? '1 1 auto' : '0 0 190px' }),
  overflow: 'hidden',
}));

const CenterAlignTableCell = styled(TableCell)(({ isMobileView, isIntermediateView }) => ({
  fontWeight: 'bold',
  border: 'none',
  fontSize: '14px',
  padding: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: 'white',
  ...(isIntermediateView ? { width: 'auto' } : { width: isMobileView ? '70px' : '160px' }),
}));

const NarrowTableCell = styled(TableCell)(({ isMobileView, isIntermediateView }) => ({
  fontWeight: 'bold',
  border: 'none',
  fontSize: '14px',
  // 👇 no agregamos padding aquí para no duplicar el gutter del Row
  padding: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: 'white',
  ...(isIntermediateView ? { width: 'auto' } : { width: isMobileView ? '60px' : '100px' }),
}));

const StyledTableSortLabel = styled(TableSortLabel)(() => ({
  color: 'white',
  '&.Mui-active': { color: 'white' },
  '& .MuiTableSortLabel-icon': {
    color: 'white !important',
    marginLeft: '4px',
  },
  // gap más chico entre texto y flecha SOLO en intermedio
  '@media (min-width:1200px) and (max-width:1387px)': {
    '& .MuiTableSortLabel-icon': { marginLeft: '2px' },
  },
}));

const OutsideTableCell = styled(TableCell)(({ isMediumScreen }) => ({
  position: 'absolute',
  right: '3%',
  top: '50%',
  transform: 'translateY(-50%)',
  fontWeight: 'bold',
  border: 'none',
  fontSize: '14px',
  display: 'flex-end',
  color: 'white',
  width: '100px',
}));

const FirmsTable = ({ firms, sortBy, setSortBy }) => {
  const navigate = useNavigate();

  // 🔹 Breakpoints
  const isSmallScreen = useMediaQuery('(max-width:980px)');
  const isIntermediateView = useMediaQuery('(min-width:1200px) and (max-width:1400px)');
  // ⚠️ Intermedio ya NO es "mobile"
  const isMobileView = isSmallScreen;

  const isMediumScreen = useMediaQuery('(max-width:1200px)');
  const isSpecificScreen = useMediaQuery('(max-width:1250px)');

  // 🔹 Elipsis en móvil y siguiente (≤900px)
  const isMobileOrNext = useMediaQuery('(max-width:900px)');

  const handleSort = (field) => {
    setSortBy({
      field,
      direction: sortBy.direction === 'asc' ? 'desc' : 'asc',
    });
  };

  const handleClick = (countrySlug, firmSlug) => {
    if (!firmSlug) return;
    if (countrySlug) {
      navigate(`/market-firm/${countrySlug}/${firmSlug}`);
      return;
    }
  };

  return (
    <TableContainer
      component={Paper}
      style={{
        border: 'none',
        boxShadow: 'none',
        backgroundColor: '#03263D',
        overflowX: isMediumScreen ? 'auto' : 'hidden',
        overflowY: 'hidden',
        width: '100%',
      }}
    >
      <Table style={{ width: '100%' }}>
        <TableHead>
          <StyledTableHeadRow isIntermediateView={isIntermediateView}>
            <NarrowTableCell isMobileView={isMobileView} isIntermediateView={isIntermediateView}>
              <StyledTableSortLabel
                active={sortBy.field === 'position'}
                direction={sortBy.field === 'position' ? sortBy.direction : 'asc'}
                onClick={() => handleSort('position')}
              >
                {isMobileView ? 'Pos' : 'Position'}
              </StyledTableSortLabel>
            </NarrowTableCell>

            <LeftAlignTableCell isMobileView={isMobileView} isIntermediateView={isIntermediateView}>
              <StyledTableSortLabel
                active={sortBy.field === 'institutionName'}
                direction={sortBy.field === 'institutionName' ? sortBy.direction : 'asc'}
                onClick={() => handleSort('institutionName')}
              >
                Name
              </StyledTableSortLabel>
            </LeftAlignTableCell>

            <CenterAlignTableCell isMobileView={isMobileView} isIntermediateView={isIntermediateView}>
              <StyledTableSortLabel
                active={sortBy.field === 'country'}
                direction={sortBy.field === 'country' ? sortBy.direction : 'asc'}
                onClick={() => handleSort('country')}
              >
                Country
              </StyledTableSortLabel>
            </CenterAlignTableCell>

            {/* Región visible en intermedio y desktop (no en small) */}
            {(!isSmallScreen || isIntermediateView) && (
              <CenterAlignTableCell isMobileView={isMobileView} isIntermediateView={isIntermediateView}>
                <StyledTableSortLabel
                  active={sortBy.field === 'region'}
                  direction={sortBy.field === 'region' ? sortBy.direction : 'asc'}
                  onClick={() => handleSort('region')}
                >
                  Region
                </StyledTableSortLabel>
              </CenterAlignTableCell>
            )}

            {/* Total Score sin wrapper extra */}
            <CenterAlignTableCell isMobileView={isMobileView} isIntermediateView={isIntermediateView}>
              <StyledTableSortLabel
                active={sortBy.field === 'totalScore'}
                direction={sortBy.field === 'totalScore' ? sortBy.direction : 'asc'}
                onClick={() => handleSort('totalScore')}
              >
                Total Score
              </StyledTableSortLabel>
            </CenterAlignTableCell>
          </StyledTableHeadRow>
        </TableHead>

        <TableBody>
          {firms.map((firm) => {
            const MAX_NAME_LENGTH = 26;

            // 🔹 Truncar también en intermedio
            const shouldTruncate =
              (isMobileOrNext || isIntermediateView) &&
              firm.institutionName.length > MAX_NAME_LENGTH;

            const firmName = shouldTruncate
              ? firm.institutionName.substring(0, MAX_NAME_LENGTH) + '…'
              : firm.institutionName;

            return (
              <StyledTableRow key={firm.id} isIntermediateView={isIntermediateView}>
                <NarrowTableCell isMobileView={isMobileView} isIntermediateView={isIntermediateView}>
                  {firm.position}
                </NarrowTableCell>

                <LeftAlignTableCell isMobileView={isMobileView} isIntermediateView={isIntermediateView}>
                  {(isMobileView || isIntermediateView) ? (
                    <div
                      style={{
                        width: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        gap: '6px',
                      }}
                      onClick={() => handleClick(firm.countrySlug, firm.slug)}
                    >
                      <div
                        style={{
                          width: '100%',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          cursor: 'pointer',
                        }}
                        title={firm.institutionName}
                      >
                        {firmName}
                      </div>
                      <Button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleClick(firm.countrySlug, firm.slug);
                        }}
                        style={{
                          fontSize: '12px',
                          fontWeight: 'bold',
                          color: 'white',
                          whiteSpace: 'nowrap',
                          textDecoration: 'underline',
                          textDecorationThickness: '2px',
                          padding: 0,
                          minWidth: 0,
                          alignSelf: 'flex-start',
                        }}
                      >
                        Profile
                      </Button>
                    </div>
                  ) : (
                    firm.institutionName
                  )}
                </LeftAlignTableCell>

                <CenterAlignTableCell isMobileView={isMobileView} isIntermediateView={isIntermediateView}>
                  <div
                    style={{
                      width: '24px',
                      height: '24px',
                      borderRadius: '50%',
                      overflow: 'hidden',
                    }}
                  >
                    <ReactCountryFlag
                      countryCode={countryCodeMapping[firm.country.id] || 'US'}
                      svg
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      title={firm.country.name}
                    />
                  </div>
                </CenterAlignTableCell>

                {(!isSmallScreen || isIntermediateView) && (
                  <CenterAlignTableCell isMobileView={isMobileView} isIntermediateView={isIntermediateView}>
                    {firm.country.region}
                  </CenterAlignTableCell>
                )}

                <CenterAlignTableCell isMobileView={isMobileView} isIntermediateView={isIntermediateView}>
                  {firm.totalScore ? firm.totalScore.toFixed(2) : 'N/A'}
                </CenterAlignTableCell>

                {(!isMobileView && !isIntermediateView) && (
                  <OutsideTableCell isMediumScreen={isMediumScreen}>
                    <Button
                      onClick={() => handleClick(firm.countrySlug, firm.slug)}
                      style={{
                        fontSize: '12px',
                        fontWeight: 'bold',
                        color: 'white',
                        whiteSpace: 'nowrap',
                        textDecoration: 'underline',
                        textDecorationThickness: '2px',
                        marginLeft: (isSpecificScreen || isMediumScreen) ? '50px' : '15px',
                      }}
                    >
                      {(isSpecificScreen || isMediumScreen) ? 'Profile' : 'Firm Profile'}
                    </Button>
                  </OutsideTableCell>
                )}
              </StyledTableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default FirmsTable;
